/* StyledGenie customer-service chat.
   Talks to POST <api>/api/support/chat. The conversation lives in this tab (sessionStorage),
   so replies never wait on a database. All text is inserted as text nodes (no innerHTML). */
(function () {
  'use strict';

  const root = document.querySelector('[data-sg-support-chat]');
  if (!root || root._sgReady) return;
  root._sgReady = true;

  const api = (root.dataset.api || '').replace(/\/+$/, '');
  const supportEmail = root.dataset.supportEmail || 'info@styledgenie.com';
  const payments = (root.dataset.payments || '').split(',').map((s) => s.trim()).filter(Boolean);
  let strings = {};
  try { strings = JSON.parse(root.querySelector('[data-sg-sc-strings]').textContent); } catch (e) { strings = {}; }
  // Shopify's translation filter HTML-escapes text (e.g. ' becomes &#39;); decode it back to plain text.
  const decoder = document.createElement('textarea');
  const decode = (s) => { decoder.innerHTML = String(s); return decoder.value; };
  Object.keys(strings).forEach((k) => { strings[k] = decode(strings[k]); });
  const t = (key, fallback) => strings[key] || fallback;

  const bubble = root.querySelector('[data-sg-sc-open]');
  const panel = root.querySelector('.sg-sc__panel');
  const closeBtn = root.querySelector('[data-sg-sc-close]');
  const log = root.querySelector('[data-sg-sc-log]');
  const status = root.querySelector('[data-sg-sc-status]');
  const chips = root.querySelector('[data-sg-sc-chips]');
  const form = root.querySelector('[data-sg-sc-form]');
  const input = root.querySelector('#sg-sc-input');
  const sendBtn = form.querySelector('.sg-sc__send');
  const resetBtn = root.querySelector('[data-sg-sc-reset]');

  const HISTORY_KEY = 'sg_sc_history_v1';
  const VISITOR_KEY = 'sg_chat_visitor_v1';
  const MAX_SENT = 16;
  const TIMEOUT_MS = 40000;

  const store = {
    get(area, key) { try { return window[area].getItem(key); } catch (e) { return null; } },
    set(area, key, value) { try { window[area].setItem(key, value); } catch (e) { /* private mode */ } },
    del(area, key) { try { window[area].removeItem(key); } catch (e) { /* ignore */ } },
  };

  let visitorId = store.get('localStorage', VISITOR_KEY) || '';
  let history = [];
  try { history = JSON.parse(store.get('sessionStorage', HISTORY_KEY) || '[]'); } catch (e) { history = []; }
  if (!Array.isArray(history)) history = [];
  let busy = false;

  /* ── rendering ─────────────────────────────────────── */
  const TOKEN_RE = /(https:\/\/[^\s<>"')]+|[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi;

  function appendRichText(el, text) {
    let last = 0;
    String(text).replace(TOKEN_RE, (match, _g, offset) => {
      if (offset > last) el.appendChild(document.createTextNode(text.slice(last, offset)));
      const clean = match.replace(/[.,;:!?]+$/, '');
      const trail = match.slice(clean.length);
      const a = document.createElement('a');
      if (clean.includes('@') && !clean.startsWith('http')) {
        a.href = 'mailto:' + clean;
      } else {
        a.href = clean;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
      }
      a.textContent = clean;
      el.appendChild(a);
      if (trail) el.appendChild(document.createTextNode(trail));
      last = offset + match.length;
      return match;
    });
    if (last < text.length) el.appendChild(document.createTextNode(text.slice(last)));
  }

  function renderMessage(msg) {
    const row = document.createElement('div');
    row.className = 'sg-sc__msg sg-sc__msg--' + (msg.role === 'user' ? 'user' : 'bot');
    const who = document.createElement('span');
    who.className = 'sg-visually-hidden';
    who.textContent = (msg.role === 'user' ? t('you', 'You') : t('assistant', 'Assistant')) + ': ';
    const bubbleEl = document.createElement('div');
    bubbleEl.className = 'sg-sc__bubble';
    bubbleEl.appendChild(who);
    appendRichText(bubbleEl, msg.content);
    row.appendChild(bubbleEl);

    if (msg.ticket && msg.ticket.reference) {
      const tk = document.createElement('p');
      tk.className = 'sg-sc__ticket';
      tk.textContent = t('ticket', 'Reference') + ': ' + msg.ticket.reference;
      row.appendChild(tk);
    }
    if (Array.isArray(msg.links) && msg.links.length) {
      const wrap = document.createElement('div');
      wrap.className = 'sg-sc__links';
      msg.links.forEach((link) => {
        if (!link || !/^https:\/\//.test(link.url || '')) return;
        const a = document.createElement('a');
        a.className = 'sg-sc__link';
        a.href = link.url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.textContent = link.label || link.url;
        const sr = document.createElement('span');
        sr.className = 'sg-visually-hidden';
        sr.textContent = ' (' + t('new_tab', 'opens in a new tab') + ')';
        a.appendChild(sr);
        wrap.appendChild(a);
      });
      row.appendChild(wrap);
    }
    log.appendChild(row);
    log.scrollTop = log.scrollHeight;
  }

  function renderAll() {
    log.textContent = '';
    renderMessage({ role: 'assistant', content: t('greeting', 'Hi! How can I help with your order today?') });
    history.forEach(renderMessage);
    chips.hidden = history.length > 0;
  }

  function showTyping(on) {
    let el = log.querySelector('.sg-sc__typing');
    if (on && !el) {
      el = document.createElement('div');
      el.className = 'sg-sc__msg sg-sc__msg--bot sg-sc__typing';
      el.setAttribute('aria-hidden', 'true');
      el.innerHTML = '<div class="sg-sc__bubble"><span></span><span></span><span></span></div>';
      log.appendChild(el);
      log.scrollTop = log.scrollHeight;
      status.textContent = t('typing', 'The assistant is typing…');
    } else if (!on && el) {
      el.remove();
      status.textContent = '';
    }
  }

  function save() {
    store.set('sessionStorage', HISTORY_KEY, JSON.stringify(history.slice(-40)));
  }

  /* ── sending ───────────────────────────────────────── */
  async function send(text) {
    const message = String(text || '').trim().slice(0, 1000);
    if (!message || busy) return;
    busy = true;
    sendBtn.disabled = true;
    const userMsg = { role: 'user', content: message };
    history.push(userMsg);
    save();
    chips.hidden = true;
    renderMessage(userMsg);
    showTyping(true);

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    let reply;
    try {
      const res = await fetch(api + '/api/support/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          visitor_id: visitorId || null,
          messages: history.slice(-MAX_SENT).map((m) => ({ role: m.role, content: m.content })),
          page_context: { payment_methods: payments, locale: root.dataset.locale || null },
        }),
      });
      if (res.status === 429) throw new Error('busy');
      if (!res.ok) throw new Error('http ' + res.status);
      const data = await res.json();
      if (data.visitor_id && data.visitor_id !== visitorId) {
        visitorId = data.visitor_id;
        store.set('localStorage', VISITOR_KEY, visitorId);
      }
      reply = { role: 'assistant', content: String(data.reply || ''), links: data.links || [], ticket: data.ticket || null };
    } catch (err) {
      const key = err && err.message === 'busy' ? 'busy' : 'error';
      const fallback = key === 'busy'
        ? 'You’re sending messages very quickly — please wait a moment and try again.'
        : 'Sorry, I couldn’t reach our support system. Please try again, or email ' + supportEmail + '.';
      reply = { role: 'assistant', content: t(key, fallback).replace('[email]', supportEmail), links: [], ticket: null, local: true };
    } finally {
      clearTimeout(timer);
    }
    showTyping(false);
    if (!reply.local) {
      history.push(reply);
      save();
    } else {
      history.pop(); // let the shopper resend the same message
      save();
      input.value = input.value || message;
    }
    renderMessage(reply);
    busy = false;
    sendBtn.disabled = false;
  }

  /* ── open / close ──────────────────────────────────── */
  function setOpen(open) {
    panel.hidden = !open;
    bubble.setAttribute('aria-expanded', String(open));
    root.classList.toggle('is-open', open);
    if (open) {
      if (!log.childElementCount) renderAll();
      log.scrollTop = log.scrollHeight;
      input.focus({ preventScroll: true });
    } else {
      bubble.focus({ preventScroll: true });
    }
  }

  bubble.addEventListener('click', () => setOpen(true));
  closeBtn.addEventListener('click', () => setOpen(false));
  root.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !panel.hidden) { e.stopPropagation(); setOpen(false); }
  });
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const value = input.value;
    input.value = '';
    autosize();
    send(value);
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) { e.preventDefault(); form.requestSubmit(); }
  });
  function autosize() {
    input.style.height = 'auto';
    input.style.height = Math.min(input.scrollHeight, 120) + 'px';
  }
  input.addEventListener('input', autosize);
  chips.addEventListener('click', (e) => {
    const chip = e.target.closest('[data-say]');
    if (chip) send(chip.dataset.say);
  });
  resetBtn.addEventListener('click', () => {
    history = [];
    store.del('sessionStorage', HISTORY_KEY);
    renderAll();
    input.focus({ preventScroll: true });
  });

  // Let other parts of the site open the chat, e.g. a "Contact us" link with data-sg-open-support.
  document.addEventListener('click', (e) => {
    const opener = e.target.closest && e.target.closest('[data-sg-open-support]');
    if (opener) { e.preventDefault(); setOpen(true); }
  });
})();
